import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import { ProgressBar } from 'ui-components/components/progress-bar';
import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { noop } from 'ui-components/default-props/noop';
import { Button } from 'ui-components/components/button';
import { AddressPropType } from '../../../../prop-types/address';
import { messages } from './messages';

import style from './address-validation.module.less';

const validatedComponent = (message, className, count) => (
  <div className={classnames(style.address, className)}>
    <span className={style['validated-message']}>
      {count} {message}
    </span>
  </div>
);

const NotValidated = ({ count }) => validatedComponent(<FormattedMessage {...messages.notValidated} />, style['not-validated'], count);

const Verified = ({ count }) => validatedComponent(<FormattedMessage {...messages.succeeded} />, style['address-validated'], count);

const ErrorState = ({ count }) => {
  const howManyErrors = count === 1 ? messages.error : messages.errors;
  return validatedComponent(<FormattedMessage {...howManyErrors} />, style.error, count);
};

const WarningState = ({ count }) => {
  const howManyWarnings = count === 1 ? messages.warning : messages.warnings;
  return validatedComponent(<FormattedMessage {...howManyWarnings} />, style.warning, count);
};

const VALIDATED_STATES = {
  VER: Verified,
  UNV: NotValidated,
  ERR: ErrorState,
  WRN: WarningState,
};

const renderValidationStatesAndCounts = (shipTos) => {
  const shipToStatuses = shipTos.map((shipTo) => {
    return shipTo.verificationStatus;
  });

  const distinctStates = new Set(shipToStatuses);

  if (distinctStates.size === 1 && distinctStates.has(undefined)) {
    return <NotValidated count={shipToStatuses.length} />;
  }

  const stateElements = [];
  distinctStates.forEach((state) => {
    const count = shipToStatuses.filter((shipTo) => shipTo === state).length;
    stateElements.push({ status: state, count });
  });
  return stateElements.map((addressType) => {
    const AddyValidation = VALIDATED_STATES[addressType.status];
    return <AddyValidation key={addressType.status} count={addressType.count} />;
  });
};

const renderValidateButton = (shipTos, onValidateAddress, isValidatingAddress) => {
  if (isValidatingAddress) {
    return (
      <div className={style['validate-button']}>
        <div className={style['progress-bar-section']}>
          <ProgressBar indefiniteProgress theme='create' />
        </div>
      </div>
    );
  }
  return (
    <div className={style['validate-button']}>
      <Button onClick={() => onValidateAddress(shipTos)} theme='primary'>
        <div className={style['validate-button-label']}>
          <FormattedMessage {...messages.revalidateButton} />
        </div>
      </Button>
    </div>
  );
};

const AddressValidation = ({
  open = false,
  onClick = noop,
  shipTos,
  onValidateAddress,
  dragHandleProps,
  isValidatingAddress = false,
  intl,
}) => (
  <CollapsibleListSection open={open}>
    <CollapsibleListSectionHeader
      className={style['validated-address-collapsible-list']}
      dragHandleProps={dragHandleProps}
      onClick={() => onClick(open)}
      summaryData={intl.formatMessage(messages.multipleAddresses)}
      theme='white'
    >
      <FormattedMessage {...messages.validateAddresses} />
    </CollapsibleListSectionHeader>
    <CollapsibleListSectionContent>
      <div className={style['address-validation-container']}>
        <div className={style['address-validation-states']}>
          {renderValidationStatesAndCounts(shipTos)}
          <div className={style['revalidate-message']}>. Click the button below to re-validate the addresses of the selected rows.</div>
        </div>
        {renderValidateButton(shipTos, onValidateAddress, isValidatingAddress)}
      </div>
    </CollapsibleListSectionContent>
  </CollapsibleListSection>
);

AddressValidation.propTypes = {
  open: PropTypes.bool,
  onClick: PropTypes.func,
  shipTos: PropTypes.arrayOf(AddressPropType).isRequired,
  onValidateAddress: PropTypes.func.isRequired,
  dragHandleProps: PropTypes.object,
  isValidatingAddress: PropTypes.bool,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(AddressValidation);
